import React, { useContext, useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import Tab from '@material-ui/core/Tab'
import Tabs from '@material-ui/core/Tabs'
import get from 'lodash/get'
import { useLocation } from 'react-router-dom'
import 'react-table/react-table.css'
import { EQUIPMENT_DETAIL_PROPERTY_WIDTH } from '../../../../Constants'
import ResponsiveDrawer from '../../../../fhg/components/ResponsiveDrawer'
import Typography from '../../../../fhg/components/Typography'
// removed admin gating for WallOfText tab
import DetailTab from './DetailTab'
import ValuationTab from './ValuationTab'
import IMSFormTab from './IMSFormTab'
import WallOfTextTab from './WallOfTextTab'
import { useAssetDrawerStore } from './assetDrawerStore'
import useEvaluationData from '../../dashboard/eval_components/fetchEvalItemhook'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'

const useStyles = makeStyles(
    (theme) => ({
        drawer: {
            height: '100%',
        },
        drawerPaper: {
            maxWidth: '100%',
            height: '100%',
            [theme.breakpoints.down(475)]: {
                width: '100%',
            },
        },
        indicatorStyle: {
            backgroundColor: theme.palette.button.standard.secondary,
        },
        tabsStyle: {
            backgroundColor: theme.palette.environment.light.level1.base,
            borderBottom: `1px solid ${theme.palette.environment.light.level1.accent}`,
            width: '100%',
            '& .MuiTabs-flexContainer': {
                width: '100%',
                justifyContent: 'space-between',
            },
            '& .MuiTab-root': {
                flex: 1,
                maxWidth: 'none',
                minWidth: 0,
                padding: '6px 8px',
            },
        },
    }),
    { name: 'AssetPropertiesDrawer' }
)

function AssetPropertiesDrawer(props) {
    const {
        evalItem = {},
        evaluation,
        isOpen,
        showThumbnail = true,
        showEquipmentTitle = true,
        width = EQUIPMENT_DETAIL_PROPERTY_WIDTH,
        breakpoint = 'xs',
        onClose,
        onSaving,
        onResize,
    } = props

    const classes = useStyles()
    const theme = useTheme()
    const location = useLocation()
    const { isZoomed, isEdit, isNew } = get(location, 'state', {})
    const { evaluationData } = useEvaluationData(evalItem?.item_id)
    const customerNumber = get(evaluation, 'evaluation.owner_id', '')

    // Static tab configuration; Wall of Text available for all users
    const tabConfig = [
        { key: 'detail', index: 0 },
        { key: 'imsform', index: 1 },
        { key: 'walloftext', index: 2 },
        { key: 'valuation', index: 3 },
    ]

    const [selectedTabIndex, setSelectedTabIndex] = useState(() => {
        const savedTab = localStorage.getItem('assetDrawerSelectedTab')
        return savedTab !== null ? parseInt(savedTab, 10) : 0
    })

    const { setIsTabClicked } = useAssetDrawerStore()

    useEffect(() => {
        setTimeout(() => {
            setIsTabClicked(selectedTabIndex === 1)
        }, 0)
    }, [selectedTabIndex, setIsTabClicked])

    useEffect(() => {
        localStorage.setItem(
            'assetDrawerSelectedTab',
            selectedTabIndex.toString()
        )

        setIsTabClicked(selectedTabIndex === 1)
    }, [selectedTabIndex, setIsTabClicked])

    /**
     * Handle the changes to the selected tab.
     * @param event The event that selected a new tab.
     * @param newSelectedTabIndex The index of the selected tab.
     */
    const handleChange = (event, newSelectedTabIndex) => {
        setSelectedTabIndex(newSelectedTabIndex)
    }

    const renderTabContent = () => {
        const currentTab = tabConfig.find(tab => tab.index === selectedTabIndex)

        switch (currentTab?.key) {
            case 'detail':
                return (
                    <DetailTab
                        evalItem={{...evalItem, itemData: evaluationData}}
                        evaluation={evaluation}
                        showThumbnail={showThumbnail}
                        showEquipmentTitle={showEquipmentTitle}
                        width={width}
                        onClose={onClose}
                    />
                )
            case 'imsform':
                return (
                    <IMSFormTab
                        evalItem={evalItem || {}}
                        customerNumber={customerNumber}
                        evaluationData={evaluationData}
                    />
                )
            case 'walloftext':
                return (
                    <WallOfTextTab
                        evalItem={evalItem || {}}
                        evaluation={evaluation}
                    />
                )
            case 'valuation':
                return (
                    <ValuationTab
                        evalItem={evalItem || {}}
                        evaluation={evaluation}
                        onSaving={onSaving}
                    />
                )
            default:
                return null
        }
    }

    const minWidth = 300
    const maxWidth = 800
    const isDraggingRef = useRef(false)
    const startXRef = useRef(0)
    const startWidthRef = useRef(width)

    const handleMouseMove = (e) => {
        if (!isDraggingRef.current) return
        const delta = startXRef.current - e.clientX
        const next = Math.min(maxWidth, Math.max(minWidth, startWidthRef.current + delta))
        onResize && onResize(next)
    }

    const endDrag = () => {
        if (!isDraggingRef.current) return
        isDraggingRef.current = false
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', endDrag)
    }

    const beginDrag = (e) => {
        isDraggingRef.current = true
        startXRef.current = e.clientX
        startWidthRef.current = width
        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', endDrag)
    }

    return (
        <ResponsiveDrawer
            isTemporary={isZoomed || isEdit || isNew}
            breakpoint={breakpoint}
            isOpen={isOpen && !isEdit && !isNew}
            sizeWidth={`${width}px`}
            onClose={onClose}
            classes={{
                drawer: classes.drawer,
                drawerPaper: classes.drawerPaper,
            }}
        >
            <div
                onMouseDown={beginDrag}
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 14,
                    cursor: 'col-resize',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderLeft: `1px solid ${theme.palette.environment.light.level1.accent}`,
                }}
            >
                <DragIndicatorIcon
                    style={{
                        fontSize: 16,
                        color: theme.palette.environment.light.level1.accent,
                        pointerEvents: 'none',
                    }}
                />
            </div>
            <Tabs
                className={classes.tabsStyle}
                value={selectedTabIndex}
                onChange={handleChange}
                variant="fullWidth"
                classes={{ indicator: classes.indicatorStyle }}
            >
                <Tab
                    label={
                        <Typography
                            className={'secondary-minimal-tab-small'}
                            id={'equipmentList.equipment.label'}
                        />
                    }
                    onClick={() => setIsTabClicked(false)}
                />
                <Tab
                    label={
                        <Typography
                            className={'secondary-minimal-tab-small'}
                            id={'equipmentList.IMSform.label'}
                        />
                    }
                    onClick={() => setIsTabClicked(true)}
                />
                <Tab
                    label={
                        <Typography
                            className={'secondary-minimal-tab-small'}
                            id={'equipmentList.wallOfText.label'}
                        />
                    }
                    onClick={() => setIsTabClicked(false)}
                />
                <Tab
                    label={
                        <Typography
                            className={'secondary-minimal-tab-small'}
                            id={'equipmentList.evaluation.label'}
                        />
                    }
                    onClick={() => setIsTabClicked(false)}
                />
            </Tabs>
            {renderTabContent()}
        </ResponsiveDrawer>
    )
}

AssetPropertiesDrawer.propTypes = {
    evalItem: PropTypes.object,
    evaluation: PropTypes.object,
    isOpen: PropTypes.bool,
    showThumbnail: PropTypes.bool,
    showEquipmentTitle: PropTypes.bool,
    width: PropTypes.number,
    breakpoint: PropTypes.string,
    onClose: PropTypes.func,
    onSaving: PropTypes.func,
    onResize: PropTypes.func,
}

export default AssetPropertiesDrawer
